import React, { useState, useEffect } from 'react';
import { Printer, FileText, AlertTriangle } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import axios from 'axios';
import dayjs from 'dayjs';
import { PageHeader } from '../components/ui/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { StatTile } from '../components/ui/stat-tile';
import { Alert, AlertDescription } from '../components/ui/alert';
import { EmptyState } from '../components/ui/empty-state';

const API_URL = process.env.REACT_APP_API_URL || 'https://monitoring.leetcode.uz/api';

function PrinterActivityPage() {
  const [printJobs, setPrintJobs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [jobsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/printer/jobs?limit=100`),
        axios.get(`${API_URL}/printer/summary`),
      ]);
      setPrintJobs(jobsRes.data.data);
      setSummary(summaryRes.data.data);
    } catch (err) {
      console.error('Failed to load printer data:', err);
    } finally {
      setLoading(false);
    }
  };

  const printerChartData = summary
    ? Object.entries(summary.printers).map(([name, count]) => ({
        printer: name.length > 20 ? name.substring(0, 20) + '...' : name,
        jobs: count,
      }))
    : [];

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center text-sm text-muted-foreground">
        Loading...
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      <PageHeader
        title="Printer Activity"
        description="Monitor print jobs and detect sensitive document printing"
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Total Print Jobs" value={summary?.total_jobs || 0} icon={Printer} tone="primary" />
        <StatTile label="Total Pages" value={summary?.total_pages || 0} icon={FileText} tone="default" />
        <StatTile
          label="Sensitive Jobs"
          value={summary?.sensitive_jobs || 0}
          icon={AlertTriangle}
          tone="warning"
        />
        <StatTile
          label="High Risk"
          value={summary?.high_risk_jobs || 0}
          icon={AlertTriangle}
          tone="destructive"
        />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Usage by Printer</CardTitle>
          </CardHeader>
          <CardContent>
            {printerChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={printerChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="printer" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <RTooltip />
                  <Legend />
                  <Bar dataKey="jobs" fill="#8884d8" name="Print Jobs" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Alert variant="info">
                <AlertDescription>No printer data yet</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Recent Print Jobs</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {printJobs.length === 0 ? (
              <EmptyState icon={Printer} title="No print jobs" description="No print jobs recorded yet" />
            ) : (
              <ul className="max-h-[300px] divide-y divide-border overflow-auto">
                {printJobs.slice(0, 10).map((job) => (
                  <li key={job.id} className="flex items-start gap-3 p-4">
                    <div className="pt-0.5">
                      {job.is_sensitive ? (
                        <AlertTriangle className="h-5 w-5 text-destructive" />
                      ) : (
                        <FileText className="h-5 w-5 text-primary" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="flex-1 truncate text-sm text-foreground">{job.document}</span>
                        {job.is_sensitive && <Badge variant="destructive">SENSITIVE</Badge>}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {dayjs(job.timestamp).format('MMM DD, HH:mm')} • {job.pages} pages
                      </div>
                      <div className="text-xs text-muted-foreground">Printer: {job.printer}</div>
                    </div>
                    {job.risk_score > 0 && (
                      <Badge variant={job.risk_score >= 30 ? 'destructive' : 'warning'}>
                        {job.risk_score}
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default PrinterActivityPage;
